import { useState } from "react";
import { Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { HexInput } from "../components/HexInput";
import { SwatchPreview } from "../components/SwatchPreview";
import { SimilarColors } from "../components/SimilarColors";
import { HarmonyPalettes } from "../components/HarmonyPalettes";
import { ColorAdjuster } from "../components/ColorAdjuster";
import { ThemeToggle } from "../components/ThemeToggle";
import {
  hexToRgb,
  rgbToCmyk,
  formatRgb,
  formatCmyk,
} from "../lib/color";

function PrintButton() {
  return (
    <button
      type="button"
      data-testid="print-button"
      onClick={() => window.print()}
      aria-label="Print palette"
      title="Print palette"
      className="h-9 w-9 inline-flex items-center justify-center border border-border bg-surface hover:bg-surface-hover transition-colors no-print"
    >
      <Printer className="h-4 w-4" strokeWidth={1.5} />
    </button>
  );
}

function AppHeader() {
  return (
    <header className="border-b border-border no-print">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 h-14 flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-muted">
            Chromaforge
          </span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted hidden md:inline">
            · CMYK Colour Studio
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.2em] text-muted">
            Soft proof · FOGRA39
          </span>
          <PrintButton />
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}

function AppFooter() {
  return (
    <footer className="pt-10 mt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3 no-print">
      <div className="flex items-center gap-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted">
          Chromaforge · Precision colour for print
        </span>
        <Link
          to="/about"
          data-testid="footer-about-link"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted hover:text-foreground transition-colors"
        >
          About
        </Link>
        <Link
          to="/privacy"
          data-testid="footer-privacy-link"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted hover:text-foreground transition-colors"
        >
          Privacy
        </Link>
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted">
        FOGRA39 · Coated · TIL 320% · GCR 35%
      </span>
    </footer>
  );
}

function ActiveColorMeta({ hex, onChange, rgb, cmyk }) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted">
          Active colour
        </span>
        <HexInput value={hex} onChange={onChange} />
      </div>

      <div className="grid grid-cols-2 gap-0 border border-border">
        <div className="p-4 border-r border-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted">RGB</div>
          <div className="mt-1 font-mono text-sm" data-testid="active-rgb">
            {formatRgb(rgb)}
          </div>
        </div>
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted">CMYK</div>
          <div className="mt-1 font-mono text-sm" data-testid="active-cmyk">
            {formatCmyk(cmyk)}
          </div>
        </div>
      </div>

      <p className="text-xs text-muted leading-relaxed max-w-md no-print">
        Type any HEX. The right panel renders the screen sRGB swatch against a
        FOGRA39-coated CMYK soft-proof so you can spot gamut drift before sending
        to print.
      </p>
    </div>
  );
}

export default function ColorTool() {
  const [hex, setHex] = useState("#0066ff");
  const rgb = hexToRgb(hex);
  const cmyk = rgbToCmyk(rgb);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      {/* Print-only banner — hidden on screen, shown on paper */}
      <div className="print-only hidden print-header">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted">
          Chromaforge · CMYK Palette · FOGRA39
        </div>
        <div className="font-mono text-2xl mt-1">
          Active {hex.toUpperCase()}
        </div>
        <div className="font-mono text-xs text-muted mt-0.5">
          RGB {formatRgb(rgb)} · CMYK {formatCmyk(cmyk)}
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 md:px-8 py-8 md:py-10 space-y-10">
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-start">
          <div className="lg:col-span-5">
            <ActiveColorMeta hex={hex} onChange={setHex} rgb={rgb} cmyk={cmyk} />
          </div>
          <div className="lg:col-span-7">
            <SwatchPreview hex={hex} />
          </div>
        </section>

        <SimilarColors hex={hex} onSelect={setHex} />
        <HarmonyPalettes hex={hex} onSelect={setHex} />
        <div className="no-print">
          <ColorAdjuster hex={hex} onCommit={setHex} />
        </div>

        <AppFooter />
      </main>
    </div>
  );
}
